import React, { useEffect, useState } from 'react'
import { union } from 'lodash-es'

import { SymbolButton } from '../lib/ui/SymbolButton'
import { ToolTip } from "../lib/ui/Tooltip"

import './ApproversSelector.css'

import { deleteUser, loadUsers } from '../actions/asyncActions'
import { AddApproverModal } from './AddApproverModal'
import { Approver } from '../types/entities'

interface Props {
  approvers: string[]
  setApprovers: Function
}

export const ApproversSelector = ({ approvers, setApprovers }: Props) => {
  const [avUsers, setAvUsers] = useState([] as Approver[])

  const loadAvUsers = async () => {
    const users = await loadUsers()
    setAvUsers(users as Approver[])
  }

  const [selectedAllApprovers, setSelectedAllApprovers] = useState([] as string[])
  const [selectedApprovers, setSelectedApprovers] = useState([] as string[])
  const [addApproverModalShown, showAddApproverModal] = useState(false)

  const addSelectedApprovers = () => {
    const newApprovers = union(approvers, selectedAllApprovers)
    setApprovers(newApprovers)
  }

  const removeSelectedApprovers = () => {
    const newList = approvers.filter(id => selectedApprovers.find(x => x === id) ? false : true)
    setApprovers(newList)
  }

  const deleteApprover = async () => {
    if (selectedAllApprovers.length !== 1) {
      return alert('Please select one approver to delete')
    }
    if (window.confirm(`Are you sure you want to delete this approver?`)) {
      await deleteUser(selectedAllApprovers[0])
      await loadAvUsers()
    }
  }

  const getSelectedOptions = (options: any) => {
    const values = [];
    for (let i = 0, l = options.length; i < l; i++) {
      if (options[i].selected) {
        values.push(options[i].value)
      }
    }
    return values
  }

  const handleAllApproverListChange = (e: any) => {
    const values = getSelectedOptions(e.target.options)
    setSelectedAllApprovers(values)
  }

  const handleApproversListChange = (e: any) => {
    const values = getSelectedOptions(e.target.options)
    setSelectedApprovers(values)
  }

  const getApproverName = (id: string) => {
    return avUsers.find(user => user.ID === id)!.Name
  }

  const setPrimaryApprover = () => {
    if (selectedApprovers.length !== 1) {
      return window.alert('Please select one approver to make the primary.')
    }
    const selectedId = selectedApprovers[0]
    const selectedIdIndexInApprovers = approvers.findIndex(id => id === selectedId)
    const newApprovers = approvers.slice()
    newApprovers.splice(selectedIdIndexInApprovers, 1)
    newApprovers.unshift(selectedId)
    setApprovers(newApprovers)
  }

  useEffect(() => {
    loadAvUsers()
  }, [])

  if (avUsers.length === 0) {
    return <div>Loading approvers</div>
  }

  return <div>
    {addApproverModalShown &&
      <AddApproverModal
        loadAvUsers={loadAvUsers}
        showAddApproverModal={showAddApproverModal}
      />}
    <div className='modal-section'>
      <div className='label'>
        Approver
        <SymbolButton
            icon='asterisk approver-new-btn'
            onClick={() => showAddApproverModal(true)}
          />
        <SymbolButton
            icon='cross-delete approver-delete-btn'
            onClick={() => deleteApprover()}
          />
      </div>
      <div className='form-inline'>
        <div className='value'>
          <select
            className='approver-list'
            onChange={handleAllApproverListChange}
            value={selectedAllApprovers}
            multiple
          >
            {avUsers.length && avUsers.map(avUser =>
              <option
                key={avUser.ID}
                value={avUser.ID}
              >
                {avUser.Name}
              </option>
            )}
          </select>
        </div>
        <div className='value approver-btns add-remove-btns'>
          <button onClick={addSelectedApprovers}>Add</button>
          <button onClick={removeSelectedApprovers}>Remove</button>
        </div>
        <div className='value'>
          <select
            className='approver-list approver-list-selected'
            onChange={handleApproversListChange}
            value={selectedApprovers}
            multiple
          >
            {approvers.map(approver => <option
                key={approver}
                value={approver}
              >
                {getApproverName(approver)}
              </option>
            )}
          </select>
        </div>
        <div className='value approver-btns'>
          <button className='set-primary-btn' onClick={setPrimaryApprover}>Set primary</button>
          <ToolTip
            width={350}
            text={`All approvers have 'APPROVER' permissions.
            The primary approver will receive email notifications for all timesheets associated with the project.`}/>
        </div>
      </div>
    </div>
  </div>
}